import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { instance } from '../axios/AxiosInstance';
import type {
  AgeGroupRow,
  AuthorRow,
  BookLanguageRow,
  CategoryRow,
  ListResponse,
} from '../pages/Settings/lists/singleTableListSupport';
import { settingsListApiError } from '../pages/Settings/lists/singleTableListSupport';

const APP_DATA_STORAGE_KEY = 'glubes.appData.v1';

interface AppDataStorageShape {
  bookLanguages: BookLanguageRow[];
  authors: AuthorRow[];
  categories: CategoryRow[];
  ageGroups: AgeGroupRow[];
  lastSyncedAt: string | null;
}

function sanitizeList<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function loadAppDataFromStorage(): AppDataStorageShape | null {
  try {
    const raw = localStorage.getItem(APP_DATA_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AppDataStorageShape>;
    return {
      bookLanguages: sanitizeList<BookLanguageRow>(parsed.bookLanguages),
      authors: sanitizeList<AuthorRow>(parsed.authors),
      categories: sanitizeList<CategoryRow>(parsed.categories),
      ageGroups: sanitizeList<AgeGroupRow>(parsed.ageGroups),
      lastSyncedAt: typeof parsed.lastSyncedAt === 'string' ? parsed.lastSyncedAt : null,
    };
  } catch {
    return null;
  }
}

function persistAppDataToStorage(data: AppDataStorageShape): void {
  try {
    localStorage.setItem(APP_DATA_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage write failures (quota, private mode, etc).
  }
}

async function fetchList<T>(path: string): Promise<T[]> {
  const { data } = await instance.get<ListResponse>(path);
  return Array.isArray(data.data) ? (data.data as T[]) : [];
}

export interface AppDataState {
  bookLanguages: BookLanguageRow[];
  authors: AuthorRow[];
  categories: CategoryRow[];
  ageGroups: AgeGroupRow[];
  loading: boolean;
  error: string | null;
  lastSyncedAt: string | null;
  refresh: () => Promise<void>;
}

const AppDataContext = createContext<AppDataState | undefined>(undefined);

interface AppDataProviderProps {
  children: ReactNode;
}

export function AppDataProvider({ children }: AppDataProviderProps) {
  const stored = loadAppDataFromStorage();
  const [bookLanguages, setBookLanguages] = useState<BookLanguageRow[]>(stored?.bookLanguages ?? []);
  const [authors, setAuthors] = useState<AuthorRow[]>(stored?.authors ?? []);
  const [categories, setCategories] = useState<CategoryRow[]>(stored?.categories ?? []);
  const [ageGroups, setAgeGroups] = useState<AgeGroupRow[]>(stored?.ageGroups ?? []);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(stored?.lastSyncedAt ?? null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextBookLanguages, nextAuthors, nextCategories, nextAgeGroups] = await Promise.all([
        fetchList<BookLanguageRow>('book-language'),
        fetchList<AuthorRow>('authors'),
        fetchList<CategoryRow>('category'),
        fetchList<AgeGroupRow>('age-group'),
      ]);
      const nowIso = new Date().toISOString();
      setBookLanguages(nextBookLanguages);
      setAuthors(nextAuthors);
      setCategories(nextCategories);
      setAgeGroups(nextAgeGroups);
      setLastSyncedAt(nowIso);
      persistAppDataToStorage({
        bookLanguages: nextBookLanguages,
        authors: nextAuthors,
        categories: nextCategories,
        ageGroups: nextAgeGroups,
        lastSyncedAt: nowIso,
      });
    } catch (e: unknown) {
      setError(settingsListApiError(e, 'Системийн датаг шинэчлэхэд алдаа гарлаа.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Hydrated values are shown immediately, then refreshed in background on page load.
    void refresh();
  }, [refresh]);

  const value = useMemo<AppDataState>(
    () => ({
      bookLanguages,
      authors,
      categories,
      ageGroups,
      loading,
      error,
      lastSyncedAt,
      refresh,
    }),
    [bookLanguages, authors, categories, ageGroups, loading, error, lastSyncedAt, refresh],
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

export function useAppData(): AppDataState {
  const ctx = useContext(AppDataContext);
  if (!ctx) {
    throw new Error('useAppData must be used within AppDataProvider.');
  }
  return ctx;
}
